import React, { useState } from 'react';

interface ViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  staff: any; // The staff object passed directly from the table
}

const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23e2e8f0'/%3E%3Ccircle cx='50' cy='38' r='18' fill='%2394a3b8'/%3E%3Cellipse cx='50' cy='80' rx='28' ry='20' fill='%2394a3b8'/%3E%3C/svg%3E";

const getApiBaseUrl = () => process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// Photos of a licence are the common case, so they get an inline preview;
// anything else (a scanned PDF) falls back to a file tile. Query strings on S3
// URLs mean the extension isn't always at the very end.
const isImageUrl = (url?: string) =>
  !!url && /\.(png|jpe?g|gif|webp|bmp|heic)(\?|$)/i.test(url);

const getStatusConfig = (status?: string) => {
  const s = status?.toLowerCase() || '';
  if (s === 'active') return { bg: '#dcfce7', color: '#15803d', dot: '#16a34a' };
  if (s === 'on leave') return { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' };
  if (s === 'suspended' || s === 'resigned') return { bg: '#fee2e2', color: '#b91c1c', dot: '#ef4444' };
  return { bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' };
};

const ViewManagementStaffModal: React.FC<ViewModalProps> = ({ isOpen, onClose, staff }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'hr' | 'documents'>('overview');

  if (!isOpen || !staff) return null;

  const getFullUrl = (path?: string) => path ? (path.startsWith('http') ? path : `${getApiBaseUrl()}/${path}`) : PLACEHOLDER_IMG;
  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  
  const calculateAge = (dob?: string) => {
    if (!dob) return '—';
    const today = new Date(), birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleCopyDetails = async () => {
    const text = `*${staff.full_name}*\n${staff.designation || 'Staff'}\nDepartment: ${staff.department || '—'}\nContact: ${staff.contact_number_1 || '—'}\nEmail: ${staff.email || '—'}`;
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      alert('Failed to copy');
    }
  };

  const statusCfg = getStatusConfig(staff.staff_status);
  const TABS = [
    { id: 'overview', label: 'Overview', icon: '👤' },
    { id: 'hr', label: 'HR & Payroll', icon: '📊' },
    { id: 'documents', label: 'Documents', icon: '📁' },
  ] as const;

  const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: 500, lineHeight: 1.5 }}>{value || <span style={{ color: '#cbd5e1' }}>—</span>}</div>
    </div>
  );

  const Card = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
    <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem', ...style }}>{children}</div>
  );

  const CardTitle = ({ icon, title }: { icon: string; title: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: '1rem' }}>{icon}</span>
      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
    </div>
  );

  return (
    <>
      <style>{`@media print { body * { visibility: hidden; } #printable-modal, #printable-modal * { visibility: visible; } #printable-modal { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; } .no-print { display: none !important; } }`}</style>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }} onClick={onClose}>
        <div id="printable-modal" style={{ backgroundColor: '#f8fafc', borderRadius: '16px', width: '96%', maxWidth: '1100px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 64px -12px rgba(0,0,0,0.35)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
          
          {/* Top Bar */}
          <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0.875rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="no-print">
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Management Staff Profile</span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleCopyDetails} style={{ backgroundColor: isCopied ? '#dcfce7' : '#eff6ff', color: isCopied ? '#166534' : '#1d4ed8', border: `1px solid ${isCopied ? '#bbf7d0' : '#bfdbfe'}`, padding: '0.45rem 1rem', borderRadius: '7px', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
                {isCopied ? '✅ Copied!' : '📋 Copy'}
              </button>
              <button onClick={() => window.print()} style={{ backgroundColor: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', padding: '0.45rem 1rem', borderRadius: '7px', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>🖨️ Print</button>
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
            </div>
          </div>

          {/* Hero Section */}
          <div style={{ backgroundColor: '#fff', padding: '1.5rem', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <img src={getFullUrl(staff.photo_url)} alt="Profile" onError={e => (e.currentTarget.src = PLACEHOLDER_IMG)} style={{ width: '88px', height: '88px', borderRadius: '12px', objectFit: 'cover', border: '2px solid #e2e8f0' }} />
                <div style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: statusCfg.dot, border: '2px solid white' }} />
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>{staff.full_name}</h2>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {staff.designation && <span style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', padding: '3px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600 }}>{staff.designation}</span>}
                  <span style={{ backgroundColor: statusCfg.bg, color: statusCfg.color, padding: '3px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600 }}>● {staff.staff_status || 'Active'}</span>
                  {staff.emp_id && <span style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '3px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600 }}>ID: {staff.emp_id}</span>}
                </div>
                <div style={{ marginTop: '8px', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  {staff.contact_number_1 && <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>📞 {staff.contact_number_1}</span>}
                  {staff.email && <span style={{ fontSize: '0.82rem', color: '#64748b' }}>✉️ {staff.email}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 1.5rem', display: 'flex' }} className="no-print">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} style={{ padding: '0.75rem 1rem', fontSize: '0.82rem', fontWeight: 600, border: 'none', borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent', backgroundColor: 'transparent', color: activeTab === tab.id ? '#1d4ed8' : '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>{tab.icon}</span>{tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            
            {activeTab === 'overview' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <Card>
                  <CardTitle icon="🧑" title="Personal Details" />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                    <Field label="Date of Birth" value={`${formatDate(staff.dob)} (${calculateAge(staff.dob)} yrs)`} />
                    <Field label="Gender" value={staff.gender} />
                    <Field label="Contact 2" value={staff.contact_number_2} />
                    <Field label="Department" value={staff.department} />
                    <Field label="Date of Joining" value={formatDate(staff.date_of_joining)} />
                    <Field label="Transport Contrib." value={staff.transport_contribution} />
                  </div>
                </Card>
                <Card>
                  <CardTitle icon="🪪" title="Identity Documents" />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '1rem' }}>
                    <Field label="Aadhar Number" value={staff.aadhar_number} />
                    <Field label="PAN Number" value={staff.pan_number} />
                    <Field label="Passport No." value={staff.passport_number} />
                  </div>
                </Card>
                <Card style={{ gridColumn: '1 / -1' }}>
                  <CardTitle icon="🎓" title="Educational Qualifications" />
                  {staff.qualifications?.length ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
                      {staff.qualifications.map((q: any, i: number) => (
                        <Field key={i} label={q.qualification_name || 'Qualification'} value={q.institution_name} />
                      ))}
                    </div>
                  ) : <div style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>No qualifications recorded.</div>}
                </Card>
              </div>
            )}

            {activeTab === 'hr' && (
              <Card style={{ borderColor: '#e0f2fe' }}>
                <CardTitle icon="🏦" title="Salary & Banking Details" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  <Field label="Base Salary" value={staff.monthly_base_salary ? `₹${staff.monthly_base_salary.toLocaleString()}` : undefined} />
                  <Field label="Class/Level" value={staff.class_level} />
                  <Field label="Bank Name" value={staff.bank_name} />
                  <Field label="Account Number" value={staff.bank_account_number} />
                  <Field label="Branch" value={staff.branch} />
                  <Field label="IFSC Code" value={staff.bank_ifsc_code ? <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0369a1' }}>{staff.bank_ifsc_code}</span> : undefined} />
                </div>
              </Card>
            )}

            {activeTab === 'documents' && (
              <>
              {/* Driving licence is uploaded on its own field, not into the
                  documents list, so it needs its own card or it's invisible here. */}
              <Card>
                <CardTitle icon="🚗" title="Driving License" />
                {staff.driving_license_url ? (
                  isImageUrl(staff.driving_license_url) ? (
                    <a href={getFullUrl(staff.driving_license_url)} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', textDecoration: 'none' }}>
                      <img src={getFullUrl(staff.driving_license_url)} alt="Driving License" style={{ maxWidth: '100%', maxHeight: '260px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'block' }} />
                      <span style={{ fontSize: '0.72rem', color: '#3b82f6', marginTop: '6px', fontWeight: 600, display: 'inline-block' }}>Open full size →</span>
                    </a>
                  ) : (
                    <a href={getFullUrl(staff.driving_license_url)} target="_blank" rel="noopener noreferrer" style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem 0.75rem', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', textDecoration: 'none', backgroundColor: '#fff', minWidth: '160px' }}>
                      <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', marginBottom: '0.6rem' }}>📄</div>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>Driving License</div>
                      <span style={{ fontSize: '0.72rem', color: '#3b82f6', marginTop: '6px', fontWeight: 600 }}>View →</span>
                    </a>
                  )
                ) : <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8' }}>No driving license uploaded.</div>}
              </Card>
              <Card>
                <CardTitle icon="📁" title="Uploaded Documents" />
                {staff.documents?.length ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
                    {staff.documents.map((doc: any) => (
                      <a key={doc.id} href={getFullUrl(doc.file_url)} target="_blank" rel="noopener noreferrer" style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem 0.75rem', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', textDecoration: 'none', backgroundColor: '#fff' }}>
                        <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', marginBottom: '0.6rem' }}>📄</div>
                        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', whiteSpace: 'nowrap' }} title={doc.document_name}>{doc.document_name}</div>
                        <span style={{ fontSize: '0.72rem', color: '#3b82f6', marginTop: '6px', fontWeight: 600 }}>View →</span>
                      </a>
                    ))}
                  </div>
                ) : <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No documents uploaded.</div>}
              </Card>
              </>
            )}

          </div>
        </div>
      </div>
    </>
  );
};

export default ViewManagementStaffModal;